import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryEntry from "@/pages/home/components/CategoryEntry";
import FeaturedSection from "@/pages/home/components/FeaturedSection";
import { getFeatured, getTopRated, getMediaByType } from "@/utils/media";
import { useSEO } from "@/hooks/useSEO";

export default function Home() {
  useSEO({
    title: "西瓜影院 - 高清电影电视剧免费在线观看平台",
    description:
      "西瓜影院为您提供丰富的电影、电视剧资源，涵盖最新影视作品与经典佳片，方便用户在线观看和查询影片信息，打造优质的观影体验。",
    keywords:
      "西瓜影院,在线电影,电视剧,电影下载,影视资源,高清电影,影视平台,观影网站",
  });

  const featured = getFeatured();
  const hotList = getTopRated(undefined, 6);
  const movieCount = getMediaByType("movie").length;
  const tvCount = getMediaByType("tv").length;

  return (
    <div className="pb-4">
      <HeroCarousel items={featured} />

      <div className="mx-auto mt-10 px-4 md:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Left: main content */}
          <div className="flex flex-col gap-10 lg:col-span-2">
            <section>
              <div className="mb-5 flex items-center gap-2">
                <span className="flex h-6 w-1 rounded-full bg-accent-500" />
                <h2 className="font-heading text-xl font-700 text-foreground-950 md:text-2xl">热门分类快捷入口</h2>
              </div>
              <CategoryEntry movieCount={movieCount} tvCount={tvCount} />
            </section>

            <FeaturedSection />
          </div>

          {/* Right: sidebar */}
          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-primary-500/40 bg-primary-500/10 p-5">
              <div className="flex items-center gap-2 text-primary-400">
                <i className="ri-user-heart-line text-lg" aria-hidden="true"></i>
                <h3 className="font-700">登录提示</h3>
              </div>
              <p className="mt-3 text-sm leading-6 text-foreground-300">
                登录后可同步收藏与观看记录。本站为演示站点，暂未开放真实登录，收藏功能仅保存在本机浏览器中。
              </p>
              <button
                type="button"
                disabled
                className="mt-4 h-10 w-full cursor-not-allowed rounded-md bg-primary-500/60 text-sm font-500 text-background-950"
                title="演示环境，暂未开放登录"
              >
                立即登录（演示）
              </button>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="mb-4 flex items-center gap-2 font-700 text-foreground-950">
                <i className="ri-apps-2-line text-lg text-accent-400" aria-hidden="true"></i>
                分类入口
              </h3>
              <div className="flex flex-col gap-2">
                <Link
                  to="/category/movie"
                  className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-foreground-300 transition-colors hover:bg-background-200 hover:text-primary-400"
                >
                  <i className="ri-film-line text-lg" aria-hidden="true"></i>
                  电影分类
                  <span className="ml-auto text-xs text-foreground-500">{movieCount} 部</span>
                </Link>
                <Link
                  to="/category/tv"
                  className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-foreground-300 transition-colors hover:bg-background-200 hover:text-primary-400"
                >
                  <i className="ri-tv-2-line text-lg" aria-hidden="true"></i>
                  电视剧分类
                  <span className="ml-auto text-xs text-foreground-500">{tvCount} 部</span>
                </Link>
                <Link
                  to="/search"
                  className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-foreground-300 transition-colors hover:bg-background-200 hover:text-primary-400"
                >
                  <i className="ri-search-line text-lg" aria-hidden="true"></i>
                  全站搜索
                </Link>
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="mb-4 flex items-center gap-2 font-700 text-foreground-950">
                <i className="ri-fire-line text-lg text-primary-400" aria-hidden="true"></i>
                全站热榜
              </h3>
              <ol className="flex flex-col gap-3">
                {hotList.map((item, idx) => (
                  <li key={item.id}>
                    <Link to={`/detail/${item.id}`} className="group flex items-center gap-3">
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-700 ${
                          idx < 3 ? "bg-primary-500 text-background-950" : "bg-background-200 text-foreground-400"
                        }`}
                      >
                        {idx + 1}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground-300 transition-colors group-hover:text-primary-400">
                        {item.title}
                      </span>
                      <span className="shrink-0 text-xs font-700 text-accent-400">{item.rating.toFixed(1)}</span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>

        {/* Site introduction */}
        <section className="mt-14 rounded-lg border border-background-200 bg-background-100 p-6 md:p-10">
          <h1 className="font-heading text-2xl font-700 text-foreground-950 md:text-3xl">
            西瓜影院 · 高清电影电视剧在线观看平台
          </h1>
          <p className="mt-4 max-w-4xl text-sm leading-7 text-foreground-400 md:text-base">
            西瓜影院专注为广大影视爱好者提供丰富的电影和电视剧资源，覆盖最新上映与经典佳片。从扣人心弦的
            <strong className="font-700 text-foreground-200">动作大片</strong>、引人入胜的
            <strong className="font-700 text-foreground-200">悬疑剧集</strong>，到温暖治愈的
            <strong className="font-700 text-foreground-200">家庭故事</strong>
            ，你都能在这里找到喜欢的作品。通过多维度的分类筛选、实时搜索与清晰的影片信息展示，我们希望让每一次观影选择都变得轻松而高效。
          </p>
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-4">
            {[
              { icon: "ri-movie-2-line", value: `${movieCount}+`, label: "精选电影" },
              { icon: "ri-tv-2-line", value: `${tvCount}+`, label: "热播剧集" },
              { icon: "ri-time-line", value: "每日", label: "内容更新" },
              { icon: "ri-emotion-happy-line", value: "免费", label: "在线浏览" },
            ].map((stat) => (
              <div key={stat.label} className="rounded-lg bg-background-50 p-4 text-center">
                <span className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-accent-500/15 text-accent-400">
                  <i className={`${stat.icon} text-lg`} aria-hidden="true"></i>
                </span>
                <p className="font-heading text-xl font-700 text-foreground-950">{stat.value}</p>
                <p className="text-xs text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}